import { useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Check, X } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { AdminPageHeader } from "@/components/admin/admin-shell";
import { CrudManager } from "@/components/admin/crud-manager";
import { adminUpdate } from "@/lib/admin";
import { azError } from "@/lib/az-errors";
import { REPORT_REASONS } from "@/lib/reports";
import { cn } from "@/lib/utils";

export const Route = createFileRoute("/_authenticated/admin/sikayetler")({
  component: Page,
});

const statusLabels: Record<string, string> = {
  open: "Açıq",
  resolved: "Həll edilib",
  rejected: "Rədd edilib",
};

type FilterKey = "all" | "sikayet" | "teklif" | "sual" | "elan";

const filters: { key: FilterKey; label: string }[] = [
  { key: "all", label: "Hamısı" },
  { key: "sikayet", label: "Şikayət" },
  { key: "teklif", label: "Təklif" },
  { key: "sual", label: "Sual" },
  { key: "elan", label: "Elan şikayətləri" },
];

/** Qeydin növünü mövcud məlumatdan müəyyən edir (backend dəyişmir). */
function kindOf(r: Record<string, any>): Exclude<FilterKey, "all"> {
  const text = `${r["reason"] ?? ""} ${r["details"] ?? ""}`.toLocaleLowerCase("az-AZ");
  if (text.includes("təklif") || text.includes("teklif")) return "teklif";
  if (text.includes("sual")) return "sual";
  if (r["listing_id"]) return "elan";
  return "sikayet";
}

function Page() {
  const qc = useQueryClient();
  const [active, setActive] = useState<FilterKey>("all");
  const setStatus = useMutation({
    mutationFn: ({ id, status }: { id: string; status: string }) =>
      adminUpdate("reports", id, { status }),
    onSuccess: () => {
      toast.success("Şikayət statusu yeniləndi");
      qc.invalidateQueries({ queryKey: ["admin", "reports"] });
    },
    onError: (e) => toast.error(azError(e)),
  });

  return (
    <div>
      <AdminPageHeader
        title="Mesajlar (Dəstək)"
        description="İstifadəçilərin şikayət, təklif və sualları"
      />
      <CrudManager
        table="reports"
        addLabel="Yeni qeyd"
        emptyText="Şikayət yoxdur."
        toolbar={(rows) => (
          <div className="no-scrollbar -mx-4 flex gap-2 overflow-x-auto px-4 pb-1 md:mx-0 md:px-0">
            {filters.map((f) => {
              const count =
                f.key === "all" ? rows.length : rows.filter((r) => kindOf(r) === f.key).length;
              const on = active === f.key;
              return (
                <button
                  key={f.key}
                  type="button"
                  onClick={() => setActive(f.key)}
                  className={cn(
                    "flex h-10 shrink-0 items-center gap-2 rounded-full border px-4 text-xs font-bold shadow-card transition-colors",
                    on
                      ? "border-primary bg-primary text-primary-foreground"
                      : "border-border bg-card text-foreground",
                  )}
                >
                  <span>{f.label}</span>
                  <span
                    className={cn(
                      "rounded-full px-2 py-0.5 text-[11px] font-extrabold",
                      on ? "bg-primary-foreground/20" : "bg-muted text-muted-foreground",
                    )}
                  >
                    {count}
                  </span>
                </button>
              );
            })}
          </div>
        )}
        filterRows={(rows) => (active === "all" ? rows : rows.filter((r) => kindOf(r) === active))}
        fields={[
          { name: "listing_id", label: "Elan ID" },
          {
            name: "reason",
            label: "Səbəb",
            type: "select",
            options: REPORT_REASONS.map((r) => ({ value: r.label, label: r.label })),
          },
          { name: "details", label: "Ətraflı", type: "textarea" },
          {
            name: "status",
            label: "Status",
            type: "select",
            options: Object.entries(statusLabels).map(([value, label]) => ({ value, label })),
          },
        ]}
        columns={[
          { name: "created_at", label: "Tarix", render: (r) => new Date(r["created_at"]).toLocaleDateString("az-AZ") },
          { name: "reason", label: "Səbəb" },
          { name: "details", label: "Ətraflı" },
          { name: "listing_id", label: "Elan" },
          {
            name: "reporter_id",
            label: "Şikayətçi",
            render: (r) => (r["reporter_id"] ? String(r["reporter_id"]).slice(0, 8) : "—"),
          },
          {
            name: "status",
            label: "Status",
            render: (r) => (
              <Badge
                className="rounded-full"
                variant={r["status"] === "resolved" ? "secondary" : r["status"] === "rejected" ? "destructive" : "outline"}
              >
                {statusLabels[r["status"]] ?? r["status"]}
              </Badge>
            ),
          },
        ]}
        extraActions={(r) => (
          <>
            <Button
              size="sm"
              variant="ghost"
              className="rounded-full"
              onClick={() => setStatus.mutate({ id: String(r["id"]), status: "resolved" })}
            >
              <Check className="size-4" />
            </Button>
            <Button
              size="sm"
              variant="ghost"
              className="rounded-full"
              onClick={() => setStatus.mutate({ id: String(r["id"]), status: "rejected" })}
            >
              <X className="size-4" />
            </Button>
          </>
        )}
      />
    </div>
  );
}
